import { useCallback, useEffect, useRef, useState } from 'react';

type UseSwipeToCloseProps = {
    isOpen: boolean;
    onClose: () => void;
    threshold?: number; // Минимальное расстояние для закрытия (в пикселях)
    velocityThreshold?: number; // Минимальная скорость для закрытия (пиксели/мс)
};

type SwipeState = {
    isDragging: boolean;
    startY: number;
    currentY: number;
    deltaY: number;
    startTime: number;
    isClosing: boolean;
    animationName?: string;
};

export const useSwipeToClose = ({
    isOpen,
    onClose,
    threshold = 100,
    velocityThreshold = 0.3,
}: UseSwipeToCloseProps) => {
    const [swipeState, setSwipeState] = useState<SwipeState>({
        isDragging: false,
        startY: 0,
        currentY: 0,
        deltaY: 0,
        startTime: 0,
        isClosing: false,
    });

    const contentRef = useRef<HTMLDivElement>(null);

    // Обработчик начала касания
    const handleTouchStart = useCallback(
        (event: TouchEvent) => {
            if (!isOpen) {
                return;
            }

            const touch = event.touches[0];
            const now = Date.now();

            setSwipeState({
                isDragging: true,
                startY: touch.clientY,
                currentY: touch.clientY,
                deltaY: 0,
                startTime: now,
                isClosing: false,
            });
        },
        [isOpen],
    );

    // Обработчик движения касания
    const handleTouchMove = useCallback(
        (event: TouchEvent) => {
            if (!swipeState.isDragging || !isOpen) {
                return;
            }

            const touch = event.touches[0];
            const deltaY = touch.clientY - swipeState.startY;

            // Разрешаем только свайп вниз
            if (deltaY < 0) {
                return;
            }

            // Предотвращаем скролл страницы при свайпе
            event.preventDefault();

            setSwipeState(prev => ({
                ...prev,
                currentY: touch.clientY,
                deltaY,
            }));
        },
        [swipeState.isDragging, swipeState.startY, isOpen],
    );

    // Обработчик окончания касания
    const handleTouchEnd = useCallback(() => {
        if (!swipeState.isDragging || !isOpen) {
            return;
        }

        const { deltaY, startTime } = swipeState;
        const duration = Date.now() - startTime;
        const velocity = deltaY / duration;

        // Закрываем модальное окно если:
        // 1. Свайп превысил пороговое расстояние
        // 2. Или скорость свайпа превысила пороговое значение
        const shouldClose = deltaY > threshold || velocity > velocityThreshold;

        if (shouldClose) {
            // Создаем динамическую CSS анимацию с текущей позиции
            const animationName = `modal-swipe-out-${Date.now()}`;
            const keyframes = `
                @keyframes ${animationName} {
                    from {
                        transform: translateY(${deltaY}px);
                        opacity: 1;
                    }
                    to {
                        transform: translateY(100vh);
                        opacity: 0;
                    }
                }
            `;

            // Добавляем стили в head
            const styleElement = document.createElement('style');
            styleElement.textContent = keyframes;
            document.head.appendChild(styleElement);

            // Устанавливаем состояние закрытия для анимации
            setSwipeState(prev => ({
                ...prev,
                isDragging: false,
                isClosing: true,
                animationName, // Сохраняем имя анимации
            }));

            // Закрываем с небольшой задержкой для анимации
            setTimeout(() => {
                onClose();
                // Удаляем созданные стили
                document.head.removeChild(styleElement);
                // Сбрасываем состояние после закрытия
                setSwipeState({
                    isDragging: false,
                    startY: 0,
                    currentY: 0,
                    deltaY: 0,
                    startTime: 0,
                    isClosing: false,
                });
            }, 300); // 300ms для плавной анимации
        } else {
            // Если не закрываем, просто сбрасываем состояние
            setSwipeState({
                isDragging: false,
                startY: 0,
                currentY: 0,
                deltaY: 0,
                startTime: 0,
                isClosing: false,
            });
        }
    }, [swipeState, threshold, velocityThreshold, onClose, isOpen]);

    // Подключаем обработчики событий
    useEffect(() => {
        const element = contentRef.current;
        if (!element || !isOpen) {
            return;
        }

        // Используем passive: false для preventDefault
        element.addEventListener('touchstart', handleTouchStart, { passive: true });
        element.addEventListener('touchmove', handleTouchMove, { passive: false });
        element.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            element.removeEventListener('touchstart', handleTouchStart);
            element.removeEventListener('touchmove', handleTouchMove);
            element.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isOpen, handleTouchStart, handleTouchMove, handleTouchEnd]);

    // Вычисляем стили и классы для анимации свайпа
    const getSwipeStyles = useCallback(() => {
        const { isDragging, deltaY, isClosing } = swipeState;

        // Если идет процесс закрытия, не применяем inline стили - используем CSS анимацию
        if (isClosing) {
            return {};
        }

        if (!isDragging || deltaY <= 0) {
            // Возвращаем в исходное положение с анимацией только если не закрываемся
            return {
                transform: 'translateY(0)',
                transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            };
        }

        // Применяем трансформацию только при активном свайпе
        return {
            transform: `translateY(${deltaY}px)`,
            transition: 'none', // Убираем transition во время свайпа
        };
    }, [swipeState]);

    // Определяем CSS классы для анимации закрытия
    const getSwipeClassName = useCallback(() => {
        if (swipeState.isClosing && swipeState.animationName) {
            return 'modal__content--closing-dynamic';
        }

        return swipeState.isClosing ? 'modal__content--closing' : '';
    }, [swipeState.isClosing, swipeState.animationName]);

    // Получаем стили для динамической анимации
    const getDynamicAnimationStyle = useCallback(() => {
        if (swipeState.isClosing && swipeState.animationName) {
            return {
                animation: `${swipeState.animationName} 0.3s ease-out forwards`,
            };
        }

        return {};
    }, [swipeState.isClosing, swipeState.animationName]);

    const getModalClassName = useCallback(() => {
        return swipeState.isClosing ? 'modal--closing' : '';
    }, [swipeState.isClosing]);

    return {
        contentRef,
        swipeState,
        getSwipeStyles,
        getSwipeClassName,
        getModalClassName,
        getDynamicAnimationStyle,
    };
};
